import type { ClinicTO } from './Clinic';
import type { ClinicianTO } from './Clinician';
import { toTeamTO, type TeamTO } from './Team';

export type MyPainHealthUserStatus = {
  created: boolean;
  onboarded: boolean;
  active: boolean;
};

export type PrimaryContactSummary = {
  pc_name: string | null;
  pc_relation: string | null;
  pc_phone: string[];
};

export class PatientTO {
  id!: number;
  access_token!: string;
  actions?: { clinician_id: number | null }[];
  assigned!: boolean;
  activeInsights?: object; // TODO: define this type
  client_no?: { client_no: number | null }[];
  clinicianSurveyToday?: any[];
  clinicsData!: string;
  communication!: string;
  dob!: string;
  episodeClosures!: any[];
  recommended?: boolean;
  favouritedClinicians!: ClinicianTO[];
  favourite!: boolean;
  first_name!: string;
  last_name!: string;
  last_episode!: any;
  medsChange!: string;
  notes?: object;
  pmmEvents!: any[];
  primaryTeams!: string;
  middle_name?: string;
  clinicIds!: number[];
}

export interface PatientProfileTO {
  access_token: string;
  activeInsights?: Record<string, any>[];
  clinics: ClinicTO[];
  contact_email: string;
  created_at: string;
  dob: string;
  favourite: boolean;
  first_name: string;
  id: number;
  last_name: string;
  favouritedClinicians: ClinicianTO[];
  gender: string;
  hpii?: string;
  ihi?: string;
  ihi_record?: string;
  ihi_status?: string;
  ihi_updated_at?: string;
  lastSync?: string;
  medicare: string;
  medicare_irn: number;
  middle_name?: string;
  phone: string;
  postcode: string;
  tags: any[];
  user_meta: any[];
  usr_episodes_details: PatientProfileEpisodesDetails[];
  pcName?: string;
  pcRelation?: string;
  pcPhone?: string;
  pcEmail?: string;
  primaryContacts?: PrimaryContactSummary[];
  teams?: TeamTO[];
  medicineProfileAccountActive?: boolean;
  osaNavigatorAccountActive?: boolean;
  patientMyPainHealthUserStatus?: MyPainHealthUserStatus;
}

type PatientProfileEpisodesDetails =
  | {
      name: string;
      value: any;
    }
  | {
      id: number;
      patient_id: number;
      event_date: string;
      event_category_id: number | null;
      event_subcategory_id: number | null;
      detail: Record<string, any>;
      name: string;
      source: string | null;
      tenancy_id: number;
      episode_id: number;
      dosage_link: any | null;
    };

export const toPatientProfileTO = (data: any): PatientProfileTO => {
  return {
    access_token: data.access_token,
    activeInsights: data.activeInsights || [],
    clinics: data.clinics || [],
    contact_email: data.contact_email || '',
    created_at: data.created_at || '',
    dob: data.dob || '',
    favourite: data.favourite || false,
    first_name: data.first_name || '',
    id: data.id || 0,
    last_name: data.last_name || '',
    favouritedClinicians: data.favouritedClinicians || [],
    gender: data.gender || '',
    hpii: data.hpii || '',
    ihi: data.ihi || '',
    ihi_record: data.ihi_record || '',
    ihi_status: data.ihi_status || '',
    ihi_updated_at: data.ihi_updated_at || '',
    lastSync: data.lastSync || '',
    medicare: data.medicare || '',
    medicare_irn: data.medicare_irn || 0,
    middle_name: data.middle_name || '',
    phone: data.phone || '',
    postcode: data.postcode || '',
    tags: data.tags || [],
    user_meta: data.user_meta || [],
    usr_episodes_details: data.usr_episodes_details || [],
    pcName: data.pc_name || '',
    pcRelation: data.pc_relation || '',
    pcPhone: data.pc_phone || '',
    pcEmail: data.pc_email || '',
    primaryContacts: data.primaryContacts || data.primary_contacts || [],
    teams: data?.teams?.map(toTeamTO) || [],
    medicineProfileAccountActive: data.medicineProfileAccountActive || false,
    osaNavigatorAccountActive: data.osaNavigatorAccountActive || false,
    patientMyPainHealthUserStatus: {
      created: data.patientMyPainHealthUserStatus?.created || false,
      onboarded: data.patientMyPainHealthUserStatus?.onboarded || false,
      active: data.patientMyPainHealthUserStatus?.active || false,
    } as MyPainHealthUserStatus,
  };
};
